import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { CalendarIcon, CheckCircleIcon } from '../../components/common/Icons';

const DAILY_SLOTS = [
  { slotNumber: 1, time: '8:30 AM - 9:20 AM' },
  { slotNumber: 2, time: '9:20 AM - 10:10 AM' },
  { slotNumber: 3, time: '10:10 AM - 11:00 AM' },
  { slotNumber: 4, time: '11:15 AM - 12:05 PM' },
  { slotNumber: 5, time: '12:05 PM - 12:55 PM' },
  { slotNumber: 6, time: '1:30 PM - 2:20 PM' },
  { slotNumber: 7, time: '2:20 PM - 3:10 PM' },
  { slotNumber: 8, time: '3:10 PM - 4:00 PM' },
];

const Attendance = () => {
  // const { user } = useAuth(); // user not currently used
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const dayButtonRefs = useRef({});

  useEffect(() => {
    fetchAttendance();
  }, []);

  const fetchAttendance = async () => {
    try {
      setLoading(true);
      // Get attendance for last 30 days
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      const response = await api.get(`/attendance/student/attendance?startDate=${startDate}&endDate=${endDate}`);
      const groupedData = response.data.data || {};
      
      // Convert grouped data to flat array
      const flatData = [];
      Object.entries(groupedData).forEach(([date, sessions]) => {
        sessions.forEach(session => {
          let subjectDisplay = 'N/A';
          if (session.subject) {
            subjectDisplay = session.subject.code 
              ? `${session.subject.code} - ${session.subject.name}`
              : session.subject.name;
          }
          flatData.push({
            id: session.id,
            date: session.date,
            subject: subjectDisplay,
            slotNumber: session.slotNumber,
            status: session.attendance.status,
            remarks: session.attendance.remarks,
            staffName:
              session.attendance?.markedBy?.name ||
              session.markedBy?.name ||
              session.staff?.name ||
              session.faculty?.name ||
              session.teacher?.name ||
              'Not marked'
          });
        });
      });
      
      setAttendance(flatData);
    } catch (error) {
      console.error('Fetch attendance error:', error);
      toast.error('Failed to load attendance records');
    } finally {
      setLoading(false);
    }
  };

  const normalizeStatus = (status) => {
    if (status === 'PRESENT' || status === 'LATE' || status === 'EXCUSED') return 'PRESENT';
    if (status === 'ABSENT') return 'ABSENT';
    return 'UNMARKED';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PRESENT': return 'bg-green-100 text-green-800';
      case 'ABSENT': return 'bg-red-100 text-red-800';
      case 'LATE': return 'bg-yellow-100 text-yellow-800';
      case 'EXCUSED': return 'bg-blue-100 text-blue-800';
      case 'UNMARKED': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusDisplay = (status) => {
    if (status === 'PRESENT') return 'Present';
    if (status === 'ABSENT') return 'Absent';
    return 'Unmarked';
  };

  const getPercentageColor = (percentage) => {
    if (percentage >= 75) return 'text-green-600';
    if (percentage >= 65) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getWeekDates = (offset = 0) => {
    const now = new Date();
    const day = now.getDay();
    const mondayOffset = day === 0 ? -6 : 1 - day;
    const monday = new Date(now);
    monday.setDate(now.getDate() + mondayOffset + (offset * 7));

    return Array.from({ length: 6 }, (_, index) => {
      const date = new Date(monday);
      date.setDate(monday.getDate() + index);
      return date;
    });
  };

  const weekDates = getWeekDates(weekOffset);
  const selectedInWeek = weekDates.some((date) => date.toDateString() === selectedDate.toDateString());
  const activeDate = selectedInWeek ? selectedDate : weekDates[0];

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia('(max-width: 639px)').matches) {
      return;
    }

    const activeDayKey = activeDate.toDateString();
    const activeButton = dayButtonRefs.current[activeDayKey];

    if (activeButton) {
      activeButton.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      });
    }
  }, [activeDate, weekOffset]);

  const dayRecords = attendance
    .filter((record) => new Date(record.date).toDateString() === activeDate.toDateString())
    .sort((a, b) => (a.slotNumber || 0) - (b.slotNumber || 0));

  const slotRecordMap = dayRecords.reduce((acc, record) => {
    acc[record.slotNumber] = {
      ...record,
      status: normalizeStatus(record.status),
    };
    return acc;
  }, {});

  const displayedRecords = DAILY_SLOTS.map((slot) => {
    const existing = slotRecordMap[slot.slotNumber];
    if (existing) {
      return { ...existing, time: slot.time };
    }
    return {
      id: `unmarked-${slot.slotNumber}`,
      date: activeDate.toISOString(),
      subject: 'No subject assigned',
      slotNumber: slot.slotNumber,
      time: slot.time,
      status: 'UNMARKED',
      remarks: '',
      staffName: 'Not marked',
    };
  });

  const markedRecords = attendance
    .map((record) => ({ ...record, status: normalizeStatus(record.status) }))
    .filter((record) => record.status !== 'UNMARKED');
  const presentCount = markedRecords.filter((record) => record.status === 'PRESENT').length;
  const totalMarkedCount = markedRecords.length;
  const computedPercentage = totalMarkedCount > 0 ? Math.round((presentCount / totalMarkedCount) * 100) : 0;
  const stats = {
    totalClasses: totalMarkedCount,
    attended: presentCount,
    percentage: computedPercentage,
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="theme-page">
      <div className="theme-page-container max-w-7xl px-3 sm:px-4 lg:px-6">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mt-2 mb-5 sm:mb-6">
          <div className="theme-muted-card p-4 sm:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-on-surface-variant text-xs uppercase tracking-wider">Total Classes</p>
                <p className="text-2xl sm:text-3xl font-bold text-on-surface mt-1">{stats.totalClasses}</p>
              </div>
              <CalendarIcon className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
            </div>
          </div>
          <div className="theme-muted-card p-4 sm:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-on-surface-variant text-xs uppercase tracking-wider">Classes Attended</p>
                <p className="text-2xl sm:text-3xl font-bold text-green-600 mt-1">{stats.attended}</p>
              </div>
              <CheckCircleIcon className="h-8 w-8 sm:h-10 sm:w-10 text-green-500" />
            </div>
          </div>
          <div className="theme-muted-card p-4 sm:p-5 col-span-2 md:col-span-1">
            <p className="text-on-surface-variant text-xs uppercase tracking-wider">Attendance Status</p>
            <p className={`text-2xl sm:text-3xl font-bold mt-2 ${getPercentageColor(stats.percentage)}`}>
              {stats.percentage >= 75 ? 'Good Standing' : 'At Risk'}
            </p>
            <p className="text-sm text-on-surface-variant mt-2">Current Percentage: {stats.percentage}%</p>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)] gap-5 sm:gap-6">
          <section className="xl:col-start-1 theme-card p-4 sm:p-6">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-5">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-primary">Weekly Schedule</h1>
                <p className="text-on-surface-variant mt-1">
                  {activeDate.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })} -{' '}
                  {weekDates[weekDates.length - 1].toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })}
                </p>
              </div>
              <div className="grid grid-cols-2 sm:flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => setWeekOffset((prev) => prev - 1)}
                  className="theme-secondary-button px-3 sm:px-4 py-2 text-xs w-full"
                >
                  Prev
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setWeekOffset((prev) => prev + 1);
                  }}
                  className="theme-primary-button px-4 sm:px-5 py-2 text-xs w-full"
                >
                  Next
                </button>
              </div>
            </div>

            <div className="flex sm:grid sm:grid-cols-6 gap-2 sm:gap-3 mt-5 sm:mt-6 overflow-x-auto pb-1">
              {weekDates.map((date) => {
                const isActive = date.toDateString() === activeDate.toDateString();
                const dayKey = date.toDateString();
                return (
                  <button
                    type="button"
                    key={date.toISOString()}
                    ref={(element) => {
                      dayButtonRefs.current[dayKey] = element;
                    }}
                    onClick={() => setSelectedDate(date)}
                    className={`min-w-[92px] sm:min-w-0 rounded-xl sm:rounded-2xl border p-2.5 sm:p-3 text-center transition-all ${
                      isActive
                        ? 'bg-primary text-on-primary border-primary shadow-md'
                        : 'bg-surface-container-lowest border-outline-variant/40 text-on-surface hover:border-primary/40'
                    }`}
                  >
                    <p className={`text-[11px] font-bold tracking-wider uppercase ${isActive ? 'text-on-primary/80' : 'text-on-surface-variant'}`}>
                      {date.toLocaleDateString('en-IN', { weekday: 'short' })}
                    </p>
                    <p className="text-2xl sm:text-3xl font-bold mt-1">{date.getDate()}</p>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="xl:col-start-1 grid grid-cols-2 gap-2 sm:gap-3">
            {displayedRecords.map((record) => (
              <article key={record.id} className="theme-card p-2.5 sm:p-3.5 min-w-0 overflow-hidden">
                <div className="flex items-center justify-between gap-1.5">
                  <span className="px-2 sm:px-2.5 py-0.5 rounded-full text-[10px] sm:text-[11px] font-semibold bg-secondary-container text-on-secondary-container whitespace-nowrap max-w-full overflow-hidden truncate">
                    {record.time}
                  </span>
                  <span className={`px-2 sm:px-2.5 py-0.5 inline-flex text-[10px] sm:text-[11px] font-semibold rounded-full whitespace-nowrap max-w-full overflow-hidden truncate ${getStatusColor(record.status)}`}>
                    {getStatusDisplay(record.status)}
                  </span>
                </div>

                <div className="mt-1.5 sm:mt-2">
                  <h3 className="text-[12px] sm:text-[15px] font-semibold sm:font-bold text-on-surface leading-snug line-clamp-1 sm:line-clamp-2">
                    {record.subject || 'N/A'}
                  </h3>
                  <div className="text-[10px] sm:text-xs text-on-surface-variant mt-1 leading-tight">
                    <p className="truncate">Slot {record.slotNumber}</p>
                    <p className="truncate">Staff: {record.staffName || 'Not marked'}</p>
                  </div>
                  {record.remarks && <p className="text-[10px] sm:text-xs text-on-surface-variant mt-1 line-clamp-1 sm:line-clamp-2">Remarks: {record.remarks}</p>}
                </div>
              </article>
            ))}
          </section>

          <aside className="xl:col-start-2 xl:row-start-1 xl:row-span-2 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-4">
              <div className="theme-card p-4 sm:p-5">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-on-surface-variant mb-3">Monthly Average</p>
                <div className="flex items-center gap-4">
                  <div className="relative w-24 h-24 sm:w-28 sm:h-28 shrink-0">
                    <svg className="w-24 h-24 sm:w-28 sm:h-28 -rotate-90" viewBox="0 0 120 120">
                      <circle cx="60" cy="60" r="48" stroke="currentColor" strokeWidth="8" fill="none" className="text-outline-variant/40" />
                      <circle
                        cx="60"
                        cy="60"
                        r="48"
                        stroke="currentColor"
                        strokeWidth="8"
                        fill="none"
                        strokeLinecap="round"
                        strokeDasharray={2 * Math.PI * 48}
                        strokeDashoffset={(2 * Math.PI * 48) * (1 - Math.min(stats.percentage, 100) / 100)}
                        className="text-primary transition-all duration-500"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <p className={`text-2xl sm:text-3xl font-extrabold ${getPercentageColor(stats.percentage)}`}>{stats.percentage}%</p>
                    </div>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-on-surface">
                      {stats.percentage >= 75 ? 'Great consistency' : 'Needs improvement'}
                    </p>
                    <p className="text-xs text-on-surface-variant mt-1">
                      Keep it above <span className="font-bold text-primary">75%</span>.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-primary text-on-primary rounded-3xl p-4 sm:p-5 shadow-md">
                <p className="text-xs uppercase tracking-[0.2em] text-on-primary/80 mb-2">Daily Momentum</p>
                <p className="text-3xl sm:text-4xl font-extrabold leading-none">{stats.attended}</p>
                <p className="mt-2 text-sm text-on-primary/90">Classes attended in current range</p>
              </div>
            </div>

            <div className="theme-muted-card p-4 sm:p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-on-surface-variant">Weekly Trend</p>
                <span className="text-primary text-sm font-semibold">Live</span>
              </div>
              <div className="h-20 sm:h-24 flex items-end gap-2">
                {[36, 58, 72, 40, 63, 68].map((height, index) => (
                  <div
                    key={`${height}-${index}`}
                    className="flex-1 rounded-t-md bg-primary/20"
                    style={{ height: `${height}%` }}
                  />
                ))}
              </div>
              <div className="mt-2 grid grid-cols-6 text-[11px] text-on-surface-variant text-center">
                {['M', 'T', 'W', 'T', 'F', 'S'].map((label, index) => (
                  <span key={`${label}-${index}`}>{label}</span>
                ))}
              </div>
            </div>

            {stats.percentage < 75 && (
              <div className="rounded-3xl bg-warning-container border border-warning/20 p-4 sm:p-5">
                <p className="text-lg font-bold text-on-warning-container mb-1">Need a boost?</p>
                <p className="text-sm text-on-warning-container/80">
                  Your attendance is below the required 75%. Attend upcoming classes regularly to recover quickly.
                </p>
              </div>
            )}
          </aside>

        </div>

      </div>
    </div>
  );
};

export default Attendance;
